<p class="sr-only" aria-live="polite" data-testid="report-live-status">
  @if (liveStatus(); as status) {
    {{ liveStatusKey() | translate: status }}
  }
</p>
@if (counts(); as counts) {
  <div class="report-test-table__toolbar">
    <div
      #chipGroup
      class="report-test-table__chips"
      role="group"
      [attr.aria-label]="'reportTestTable.filter.groupAria' | translate"
    >
      @for (chip of chips; track chip) {
        <app-filter-chip
          [attr.data-testid]="'report-filter-' + chip"
          [label]="'reportTestTable.filter.' + chip | translate"
          [count]="counts[chip]"
          [countLabel]="countKey(counts[chip]) | translate: { count: counts[chip] }"
          [active]="chip === activeChip()"
          [pill]="true"
          (clicked)="selectChip(chip)"
        />
      }
    </div>
    <div class="report-test-table__search">
      <label class="sr-only" [for]="searchId">{{ 'reportTestTable.search.label' | translate }}</label>
      <mat-icon class="report-test-table__search-icon" aria-hidden="true">search</mat-icon>
      <input
        #searchField
        type="search"
        class="report-test-table__search-input"
        data-testid="report-search"
        autocomplete="off"
        [id]="searchId"
        [attr.maxlength]="searchMax"
        [placeholder]="'reportTestTable.search.placeholder' | translate"
        (input)="onSearch(searchField.value)"
      />
    </div>
  </div>
}

@if (actionErrorKey(); as actionErrorKey) {
  <div class="report-test-table__action-error" role="alert" data-testid="report-action-error">
    {{ actionErrorKey | translate }}
  </div>
}

@if (loading()) {
  <div class="report-test-table__loading" data-testid="report-test-loading">
    <mat-progress-spinner
      mode="indeterminate"
      diameter="32"
      [attr.aria-label]="'reportTestTable.loading' | translate"
    />
  </div>
} @else if (failure() === 'first') {
  <div class="report-test-table__error" role="alert" data-testid="report-test-error">
    <span>{{ 'reportTestTable.loadError' | translate }}</span>
    <button type="button" class="report-test-table__button" data-testid="report-test-retry" (click)="retry()">
      {{ 'reportTestTable.retry' | translate }}
    </button>
  </div>
} @else {
  @if (showsLegacyHint()) {
    <p class="report-test-table__hint" data-testid="report-legacy-hint">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>{{ 'reportTestTable.legacyHint' | translate }}</span>
    </p>
  }
  @if (showsReadonlyHint()) {
    <p class="report-test-table__hint" data-testid="report-readonly-hint">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>{{ 'reportTestTable.readonlyHint' | translate }}</span>
    </p>
  }

  <div class="report-test-table__card">
    <table
      class="report-test-table__table"
      data-testid="report-test-table"
      [attr.aria-label]="'reportTestTable.tableAria' | translate"
    >
      <thead>
        <tr>
          <th scope="col">{{ 'reportTestTable.column.test' | translate }}</th>
          <th scope="col" class="report-test-table__col-result">{{ 'reportTestTable.column.result' | translate }}</th>
          <th scope="col" class="report-test-table__col-duration">
            {{ 'reportTestTable.column.duration' | translate }}
          </th>
          <th scope="col" class="report-test-table__col-case">{{ 'reportTestTable.column.testCase' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        @for (item of items(); track item.id) {
          <tr
            class="report-test-table__row"
            data-testid="report-test-row"
            [class.report-test-table__row--running]="item.status === 'RUNNING'"
          >
            <td>
              @if (classname(item); as classname) {
                <div class="report-test-table__classname">{{ classname }}</div>
              }
              <div class="report-test-table__name">{{ item.name }}</div>
              @if (failureLine(item); as line) {
                @let open = isExpanded(item);
                <button
                  type="button"
                  class="report-test-table__toggle"
                  data-testid="report-test-error-toggle"
                  [attr.aria-expanded]="open"
                  [attr.aria-controls]="open ? errorId(item) : null"
                  (click)="toggleError(item)"
                >
                  <mat-icon class="report-test-table__chevron" aria-hidden="true">
                    {{ open ? 'expand_more' : 'chevron_right' }}
                  </mat-icon>
                  <span class="report-test-table__message" data-testid="report-test-message">{{ line }}</span>
                </button>
                @if (open) {
                  <pre class="report-test-table__full" data-testid="report-test-error-full" [id]="errorId(item)">{{
                    item.errorMessage
                  }}</pre>
                }
              }
            </td>
            <td class="report-test-table__col-result">
              @switch (item.status) {
                @case ('RUNNING') {
                  <app-res-pill
                    data-testid="report-test-running"
                    status="running"
                    [label]="'reportTestTable.status.RUNNING' | translate"
                    [pulsing]="true"
                  />
                }
                @case ('NOT_RUN') {
                  @if (completed()) {
                    <span class="report-test-table__not-run">{{ 'reportTestTable.status.NOT_RUN' | translate }}</span>
                  } @else {
                    <span class="report-test-table__open">{{ 'reportTestTable.status.open' | translate }}</span>
                  }
                }
                @default {
                  @let mark = statusMark(item);
                  <span
                    class="report-test-table__status"
                    role="img"
                    [attr.data-status]="mark.kind"
                    [attr.aria-label]="statusLabelKey(item) | translate"
                  >
                    <mat-icon aria-hidden="true">{{ mark.icon }}</mat-icon>
                  </span>
                }
              }
            </td>
            <td class="report-test-table__col-duration">{{ time(item) }}</td>
            <td class="report-test-table__col-case">
              @if (item.testCaseKey) {
                <app-code-id-chip [id]="item.testCaseKey" [attr.title]="item.testCaseTitle" />
                @if (item.matchVia) {
                  <span class="report-test-table__via" [attr.data-via]="item.matchVia">
                    {{ 'ciMapping.via.' + item.matchVia | translate }}
                  </span>
                }
                @if (canAssign() && item.matchVia === 'manual') {
                  <button
                    type="button"
                    class="report-test-table__unassign"
                    data-testid="ci-mapping-unassign"
                    [attr.aria-label]="'ciMapping.unassignAria' | translate: { name: item.name }"
                    [attr.title]="'ciMapping.unassign' | translate"
                    (click)="openUnassign(item)"
                  >
                    <mat-icon aria-hidden="true">link_off</mat-icon>
                  </button>
                }
              } @else if (canAssign()) {
                <button
                  type="button"
                  class="report-test-table__assign"
                  data-testid="ci-mapping-assign"
                  [attr.aria-label]="'ciMapping.assignAria' | translate: { name: item.name }"
                  (click)="openAssign(item)"
                >
                  <mat-icon aria-hidden="true">add</mat-icon>
                  {{ 'ciMapping.assign' | translate }}
                </button>
              } @else {
                <span class="report-test-table__unmatched">{{ 'reportTestTable.unmatched' | translate }}</span>
              }
              @let defects = item.defects ?? [];
              @if (item.linkedBugKey || offersBug(item) || defects.length > 0) {
                <div class="report-test-table__bug-line">
                  @if (item.linkedBugKey; as bugKey) {
                    <a
                      class="report-test-table__bug"
                      data-testid="report-test-bug-link"
                      [attr.data-case-result]="item.caseResultId"
                      target="_blank"
                      rel="noopener noreferrer"
                      [href]="bugUrl(item)"
                      [attr.aria-label]="
                        'testRunner.live.failures.bugOpenAria' | translate: { key: bugKey, test: item.name }
                      "
                    >
                      {{ bugKey }}
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                    </a>
                  } @else if (offersBug(item)) {
                    <button
                      type="button"
                      class="report-test-table__bug"
                      data-testid="report-test-bug-action"
                      aria-haspopup="dialog"
                      [attr.data-case-result]="item.caseResultId"
                      [attr.aria-label]="'testRunner.live.failures.bugCreateAria' | translate: { test: item.name }"
                      (click)="openBug(item)"
                    >
                      <mat-icon aria-hidden="true">add</mat-icon>
                      {{ 'testRunner.bug.create' | translate }}
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                    </button>
                  }
                  @if (defects.length > 0) {
                    <app-defect-ref-chips [projectId]="projectId()" [defects]="defects" />
                  }
                </div>
              }
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="4" class="report-test-table__empty" data-testid="report-test-empty">
              {{ emptyKey() | translate: { query: search() } }}
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  @if (failure() === 'more') {
    <div class="report-test-table__more report-test-table__more--error" role="alert" data-testid="report-test-error">
      <span>{{ 'reportTestTable.moreError' | translate }}</span>
      <button
        type="button"
        class="report-test-table__button"
        data-testid="report-test-retry"
        [disabled]="loadingMore()"
        (click)="retry()"
      >
        {{ 'reportTestTable.retry' | translate }}
      </button>
    </div>
  } @else if (hasMore()) {
    <div class="report-test-table__more">
      <button
        type="button"
        class="report-test-table__button"
        data-testid="report-more"
        [disabled]="loadingMore()"
        (click)="loadMore()"
      >
        {{ 'reportTestTable.more' | translate }}
      </button>
    </div>
  }
}

@if (assignTarget(); as target) {
  <app-ci-mapping-assign-dialog
    [projectId]="projectId()"
    [test]="target"
    [classname]="classname(target)"
    [busy]="assigning()"
    [errorKey]="assignErrorKey()"
    (picked)="onPicked($event)"
    (cancelled)="cancelAssign()"
  />
}

@if (bug.dialog(); as dialog) {
  <app-live-bug-dialog
    [context]="dialog.context"
    [targets]="bug.targets()"
    [submitting]="bug.submitting()"
    [error]="bug.error()"
    [existingBugKey]="bugDialogExistingKey()"
    [existingBugUrl]="bugDialogExistingUrl()"
    [preview]="bug.preview()"
    [(choice)]="bugChoice"
    (confirmed)="confirmBug($event)"
    (cancelled)="closeBug()"
  />
}

@if (unassignTarget(); as target) {
  <div #confirmHost cdkTrapFocus>
    <app-confirm-dialog
      icon="link_off"
      [visible]="true"
      [title]="'ciMapping.unassignConfirm.title' | translate"
      [message]="'ciMapping.unassignConfirm.message' | translate: { name: target.name, key: target.testCaseKey }"
      [confirmLabel]="'ciMapping.unassignConfirm.confirm' | translate"
      [cancelLabel]="'ciMapping.unassignConfirm.cancel' | translate"
      [busy]="unassigning()"
      (confirmed)="confirmUnassign()"
      (cancelled)="cancelUnassign()"
    />
  </div>
}
